import React, { useRef, useState } from "react";
import styled from "styled-components";
import genomeArtExample from "../../assets/projectImages/genomeart_example.png";

const Container = styled.div`
  height: 100vh;
  width: 100%;
  background-color: #000;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 40px;
  overflow-y: scroll;
`;

const Description = styled.p`
  max-width: 600px;
  text-align: center;
  margin-top: 20px;
  line-height: 1.4;
`;

const Disclaimer = styled.p`
  max-width: 600px;
  text-align: center;
  margin-top: 10px;
  font-size: 0.85rem;
  line-height: 1.4;
  color: #ccc;
  font-style: italic;
`;

const CanvasWrapper = styled.div`
  margin-top: 20px;
  border: 1px solid #333;
`;

const ExampleWrapper = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ExampleLabel = styled.p`
  font-size: 0.85rem;
  color: #ccc;
  margin-bottom: 8px;
`;

const ExampleImg = styled.img`
  max-width: 280px;
  width: 100%;
  border: 1px solid #333;
  border-radius: 4px;
`;

const Button = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  background: #222;
  color: #fff;
  border: 1px solid #555;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #333;
  }
`;

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function parse23andMe(text) {
  const rows = [];
  text.split(/\r?\n/).forEach((line) => {
    if (!line.trim() || line.startsWith("#")) return;
    const parts = line.trim().split(/\s+/);
    if (parts.length >= 4) {
      const chrom = parts[1].toUpperCase();
      const gt = parts[parts.length - 1].toUpperCase();
      if (/^[ACGT-]+$/.test(gt)) {
        rows.push([chrom, gt]);
      }
    }
  });
  return rows;
}

function genotypesToBytes(genotypes) {
  const mapping = { A: 0, C: 1, G: 2, T: 3, "-": 4 };
  const nibbles = [];
  genotypes.forEach((gt) => {
    const chars = gt.length === 2 ? gt : gt + gt;
    for (const ch of chars.slice(0, 2)) {
      nibbles.push(mapping[ch] ?? 4);
    }
  });
  const out = new Uint8Array(Math.ceil(nibbles.length / 2));
  for (let i = 0; i < nibbles.length; i += 2) {
    const a = nibbles[i];
    const b = i + 1 < nibbles.length ? nibbles[i + 1] : 0;
    out[i >> 1] = (a << 4) | (b & 0xf);
  }
  return out;
}

async function hashBytes(bytes) {
  const hashBuffer = await crypto.subtle.digest("SHA-256", bytes);
  return new Uint8Array(hashBuffer);
}

function paletteFromHash(hash, n = 6) {
  const palette = [];
  for (let i = 0; i < n; i++) {
    const r = hash[i * 3];
    const g = hash[i * 3 + 1];
    const b = hash[i * 3 + 2];
    palette.push(`rgb(${r},${g},${b})`);
  }
  return palette;
}

async function processGenome(text) {
  const rows = parse23andMe(text);
  const genotypes = rows.map(([, gt]) => gt);
  const bytes = genotypesToBytes(genotypes);
  const hash = await hashBytes(bytes);
  const seed = new DataView(hash.buffer).getUint32(0, false);
  const palette = paletteFromHash(hash, 6);
  return { seed, palette };
}

function drawFlowField(ctx, palette, seed, numLines = 1800, steps = 120) {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, w, h);
  const rand = mulberry32(seed);
  const field = new Array(64).fill(0).map(() => new Array(64));
  for (let y = 0; y < 64; y++) {
    for (let x = 0; x < 64; x++) {
      field[y][x] = rand() * Math.PI * 2;
    }
  }
  for (let i = 0; i < numLines; i++) {
    let x = rand() * w;
    let y = rand() * h;
    const color = palette[Math.floor(rand() * palette.length)];
    let width = 0.6 + rand() * 2;
    ctx.strokeStyle = color;
    for (let j = 0; j < steps; j++) {
      const gx = Math.floor((x / w) * 63) % 64;
      const gy = Math.floor((y / h) * 63) % 64;
      const angle = field[gy][gx];
      const x2 = x + Math.cos(angle) * 2.2;
      const y2 = y + Math.sin(angle) * 2.2;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      width = Math.max(0.5, Math.min(3.5, width + (rand() - 0.5) * 0.22));
      x = x2;
      y = y2;
      if (x < 0 || x > w || y < 0 || y > h) break;
    }
  }
}

const Genome = () => {
  const canvasRef = useRef(null);
  const [params, setParams] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const text = ev.target?.result || "";
      const p = await processGenome(text);
      setParams(p);
      const ctx = canvasRef.current.getContext("2d");
      drawFlowField(ctx, p.palette, p.seed);
    };
    reader.readAsText(file);
  };

  const handleDownload = () => {
    if (!params) return;
    const size = 1024;
    const off = document.createElement("canvas");
    off.width = size;
    off.height = size;
    const ctx = off.getContext("2d");
    drawFlowField(ctx, params.palette, params.seed);
    const link = document.createElement("a");
    link.download = "genome.png";
    link.href = off.toDataURL("image/png");
    link.click();
  };

  return (
    <Container>
      <h1>Genome Art</h1>
      <Description>
        Turn your 23andme genome.txt file into a piece of art.
      </Description>
      <Disclaimer>
        Your genetic data is processed locally in your browser, and no data is
        sent to any server. Your genome data cannot be reverse-engineered from
        the generated image.
      </Disclaimer>
      <ExampleWrapper>
        <ExampleLabel>Example</ExampleLabel>
        <ExampleImg src={genomeArtExample} alt="Example genome art output" />
      </ExampleWrapper>
      <input type="file" accept=".txt" onChange={handleFile} />
      <CanvasWrapper>
        <canvas ref={canvasRef} width={600} height={600} />
      </CanvasWrapper>
      {params && <Button onClick={handleDownload}>Save High-Res PNG</Button>}
    </Container>
  );
};

export default Genome;
